import { I18n } from '@affine/i18n';

import {
  findDatabaseByBlockId,
  getAccessibleDatabases,
  getAccessibleTrashcanDatabases,
  getWritableDatabases,
  type WorkspaceDatabaseInfo,
} from './database-discovery-bridge';

export type RelationEndpointAccessState =
  | 'write'
  | 'read'
  | 'restricted'
  | 'missing'
  | 'trashed';

const RELATION_RESTRICTED_DATABASE_TITLE_FALLBACK = 'Restricted database';
const RELATION_RESTRICTED_DOC_TITLE_FALLBACK = 'Restricted document';
const RELATION_RESTRICTED_CELL_LABEL_FALLBACK = 'Restricted relation';

function translateRelationAccessLabel(key: string, fallback: string): string {
  const translate = (I18n as unknown as Record<string, () => string>)[key];
  if (typeof translate !== 'function') {
    return fallback;
  }
  const translated = translate();
  return translated && translated !== key ? translated : fallback;
}

export function getRelationRestrictedDatabaseTitle(): string {
  return translateRelationAccessLabel(
    'com.streamlain.streamlain.relation.restricted-database',
    RELATION_RESTRICTED_DATABASE_TITLE_FALLBACK
  );
}

export function getRelationRestrictedDocTitle(): string {
  return translateRelationAccessLabel(
    'com.streamlain.streamlain.relation.restricted-document',
    RELATION_RESTRICTED_DOC_TITLE_FALLBACK
  );
}

export function getRelationRestrictedCellLabel(): string {
  return translateRelationAccessLabel(
    'com.streamlain.streamlain.relation.restricted-relation',
    RELATION_RESTRICTED_CELL_LABEL_FALLBACK
  );
}

export type RelationEndpointAccessRequest = {
  targetDatabaseId?: string;
  targetDocId?: string;
};

export type RelationEndpointAccessResult = {
  state: RelationEndpointAccessState;
  database?: WorkspaceDatabaseInfo;
  targetDocId?: string;
  docTitle: string;
  databaseTitle: string;
};

function matchesTarget(
  database: WorkspaceDatabaseInfo,
  targetDatabaseId: string,
  targetDocId?: string
): boolean {
  return (
    database.blockId === targetDatabaseId &&
    (!targetDocId || database.docId === targetDocId)
  );
}

function canReadTarget(
  targetDatabaseId: string,
  targetDocId?: string
): boolean {
  return [
    ...getAccessibleDatabases(),
    ...getAccessibleTrashcanDatabases(),
  ].some(database => matchesTarget(database, targetDatabaseId, targetDocId));
}

function canWriteTarget(
  targetDatabaseId: string,
  targetDocId?: string
): boolean {
  return getWritableDatabases().some(database =>
    matchesTarget(database, targetDatabaseId, targetDocId)
  );
}

export function resolveRelationEndpointAccess({
  targetDatabaseId,
  targetDocId,
}: RelationEndpointAccessRequest): RelationEndpointAccessResult {
  if (!targetDatabaseId) {
    return {
      state: 'missing',
      targetDocId,
      docTitle: '',
      databaseTitle: '',
    };
  }

  const database = findDatabaseByBlockId(targetDatabaseId);
  const effectiveTargetDocId = database?.docId ?? targetDocId;
  if (!database) {
    if (targetDocId) {
      return {
        state: 'restricted',
        targetDocId,
        docTitle: getRelationRestrictedDocTitle(),
        databaseTitle: getRelationRestrictedDatabaseTitle(),
      };
    }

    return {
      state: 'missing',
      targetDocId,
      docTitle: '',
      databaseTitle: '',
    };
  }

  const canWrite = canWriteTarget(targetDatabaseId, effectiveTargetDocId);
  const canRead =
    canWrite || canReadTarget(targetDatabaseId, effectiveTargetDocId);

  if (database.isInTrash) {
    if (!canRead) {
      return {
        state: 'restricted',
        targetDocId: effectiveTargetDocId,
        docTitle: getRelationRestrictedDocTitle(),
        databaseTitle: getRelationRestrictedDatabaseTitle(),
      };
    }
    return {
      state: 'trashed',
      database,
      targetDocId: database.docId,
      docTitle: database.docTitle,
      databaseTitle: database.databaseTitle,
    };
  }

  if (canWrite) {
    return {
      state: 'write',
      database,
      targetDocId: database.docId,
      docTitle: database.docTitle,
      databaseTitle: database.databaseTitle,
    };
  }

  if (canRead) {
    return {
      state: 'read',
      database,
      targetDocId: database.docId,
      docTitle: database.docTitle,
      databaseTitle: database.databaseTitle,
    };
  }

  return {
    state: 'restricted',
    targetDocId: effectiveTargetDocId,
    docTitle: getRelationRestrictedDocTitle(),
    databaseTitle: getRelationRestrictedDatabaseTitle(),
  };
}

export function canWriteRelationEndpoint(
  request: RelationEndpointAccessRequest
): boolean {
  return resolveRelationEndpointAccess(request).state === 'write';
}
